import { Link } from '@tanstack/react-router';
import { LayoutGrid, List, Search, Star } from 'lucide-react';
import type { ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';
import { Input } from '@/shared/ui/Input';

import styles from './Catalog.module.scss';
import type { ViewMode } from './useViewMode';

/*
 * Раздел-каталог — «Страницы», «Модели», «Компоненты», «Константы»: шапка с
 * действиями, поиск, плитки или список; правка — на деталке (как в старой
 * админке, решение Давида 20 октября 2026).
 */

export function CatalogHead({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className={styles['head']}>
      <div className={styles['head__text']}>
        <h1 className={styles['title']}>{title}</h1>
        {subtitle !== undefined && <p className={styles['subtitle']}>{subtitle}</p>}
      </div>
      {actions !== undefined && <div className={styles['head__actions']}>{actions}</div>}
    </header>
  );
}

export function ViewToggle({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (mode: ViewMode) => void;
}) {
  return (
    <div className={styles['toggle']} role="group" aria-label="Вид">
      <button
        type="button"
        aria-pressed={value === 'grid'}
        title="Плитки"
        onClick={() => {
          onChange('grid');
        }}
      >
        <LayoutGrid size={15} />
      </button>
      <button
        type="button"
        aria-pressed={value === 'list'}
        title="Список"
        onClick={() => {
          onChange('list');
        }}
      >
        <List size={15} />
      </button>
    </div>
  );
}

export function CatalogSearch({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className={styles['search']}>
      <Input
        prefix={<Search size={15} />}
        clearable
        placeholder={placeholder}
        aria-label={placeholder}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </div>
  );
}

export function CatalogGroup({
  title,
  count,
  children,
}: {
  title?: ReactNode;
  count?: number;
  children: ReactNode;
}) {
  return (
    <section className={styles['group']}>
      {title !== undefined && (
        <h2 className={styles['group__title']}>
          {title}
          {count !== undefined && <span>{count}</span>}
        </h2>
      )}
      {children}
    </section>
  );
}

export function CatalogGrid({ children }: { children: ReactNode }) {
  return <div className={styles['grid']}>{children}</div>;
}

/** Плитка: вся — ссылка на деталку; звёздочка и меню — поверх неё. */
export function CatalogCard({
  link,
  preview,
  icon,
  title,
  subtitle,
  badge,
  meta,
  aside,
}: {
  link: { to: string; params?: Record<string, string>; search?: Record<string, string> };
  preview?: ReactNode;
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  badge?: ReactNode;
  meta?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <div className={styles['card']}>
      <Link
        to={link.to}
        params={link.params as never}
        search={link.search as never}
        className={styles['card__link']}
      >
        {preview}
        <span className={styles['card__top']}>
          {icon !== undefined && <span className={styles['card__icon']}>{icon}</span>}
          <span className={styles['card__names']}>
            <span className={styles['card__title']}>{title}</span>
            {subtitle !== undefined && <span className={styles['card__subtitle']}>{subtitle}</span>}
          </span>
          {badge}
        </span>
        {meta !== undefined && <span className={styles['card__meta']}>{meta}</span>}
      </Link>
      {aside !== undefined && <div className={styles['card__aside']}>{aside}</div>}
    </div>
  );
}

export function NewCard({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" className={cx(styles['card'], styles['card--new'])} onClick={onClick}>
      {children}
    </button>
  );
}

export function StarButton({ active, onToggle }: { active: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      className={styles['star']}
      aria-pressed={active}
      title={active ? 'Убрать из избранного' : 'В избранное — будет в сайдбаре'}
      aria-label={active ? 'Убрать из избранного' : 'В избранное'}
      onClick={onToggle}
    >
      <Star size={16} />
    </button>
  );
}

/** Таблица списка: строка — ссылка на деталку. */
export function CatalogTable({ head, children }: { head: ReactNode[]; children: ReactNode }) {
  return (
    <div className={styles['table']}>
      <table>
        <thead>
          <tr>
            {head.map((cell, index) => (
              <th key={index}>{cell}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
